import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Pencil, X } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { getActorDisplayName } from '../../../utils/roleUtils';
import type { RoleOption } from '../../../services/api/onboardingService';
import { formatShortDate } from '../../../utils/dateUtils';
import type { UserRow } from './userManagementTypes';
import EditFacilityUserDialog, {
    type EditFacilityUserValues,
} from '../EditUserPermissions';

function displayRoleName(
    role: string,
    roleId: string | undefined,
    roles: RoleOption[]
): string {
    const option = roleId
        ? roles.find((r) => r._id === roleId)
        : roles.find((r) => r.role === role);
    return option ? getActorDisplayName(option) : role;
}

interface ActiveUsersTableProps {
    users: UserRow[];
    roles: RoleOption[];
    departmentId?: string;
    showSearches?: boolean;
    onEditUser?: (
        user: UserRow,
        values: EditFacilityUserValues
    ) => Promise<boolean | void>;
    onRevokeAccess?: (user: UserRow) => Promise<boolean | void> | boolean | void;
    emptyMessage?: string;
}

export default function ActiveUsersTable({
    users,
    roles,
    departmentId,
    showSearches = true,
    onEditUser,
    onRevokeAccess,
    emptyMessage = 'No active users yet.',
}: ActiveUsersTableProps) {
    const { t } = useTranslation();
    const [editTarget, setEditTarget] = useState<UserRow | null>(null);
    const [revokeAccessTarget, setRevokeAccessTarget] =
        useState<UserRow | null>(null);
    const [revokingAccessId, setRevokingAccessId] = useState<string | null>(
        null
    );

    const handleRevokeAccess = async () => {
        if (!revokeAccessTarget || !onRevokeAccess) return;

        setRevokingAccessId(revokeAccessTarget._id);
        try {
            const result = await onRevokeAccess(revokeAccessTarget);
            if (result !== false) setRevokeAccessTarget(null);
        } catch {
            // called handles feedback
        } finally {
            setRevokingAccessId(null);
        }
    };

    return (
        <div className="space-y-3">
            <Table containerClassName="rounded-lg border border-white/5 bg-white/5">
                <TableHeader className="bg-white/5 [&_tr]:border-white/5">
                    <TableRow className="hover:bg-transparent">
                        <TableHead>{t('facilities.activeUsersTable.user')}</TableHead>
                        <TableHead>{t('facilities.activeUsersTable.role')}</TableHead>
                        {showSearches && <TableHead>{t('facilities.activeUsersTable.searches')}</TableHead>}
                        <TableHead>{t('facilities.activeUsersTable.expiryDate')}</TableHead>
                        <TableHead className="text-right">{t('facilities.activeUsersTable.actions')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                            <TableCell
                                colSpan={showSearches ? 5 : 4}
                                className="py-10 text-center text-xs text-muted-foreground"
                            >
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        users.map((user) => {
                            const isExpired = user.status === 'inactive';
                            return (
                            <TableRow
                                key={user._id}
                                className="border-white/5"
                            >
                                <TableCell>
                                    <div>
                                        <span className="text-xs text-foreground">
                                            {user.name || user.email}
                                        </span>
                                        {user.name && (
                                            <p className="text-xs text-muted-foreground">
                                                {user.email}
                                            </p>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <span className="text-xs text-muted-foreground">
                                        {displayRoleName(user.role, user.roleId, roles)}
                                    </span>
                                </TableCell>
                                {showSearches && (
                                    <TableCell>
                                        <span className="text-xs tabular-nums text-muted-foreground">
                                            {user.searchesTotal ?? '—'}
                                            {user.searchesPerWeek != null && (
                                                <span className="ml-1 text-muted-foreground/60">
                                                    ({t('facilities.activeUsersTable.searchesPastWeek', { count: user.searchesPerWeek })})
                                                </span>
                                            )}
                                        </span>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">
                                            {user.expiryDate
                                                ? formatShortDate(user.expiryDate)
                                                : '—'}
                                        </span>
                                        {isExpired && (
                                            <Badge
                                                variant="outline"
                                                className="rounded-full border-rose-400/20 bg-rose-400/5 px-2 py-0.5 text-xs font-medium text-rose-300"
                                            >
                                                {t('facilities.activeUsersTable.expired')}
                                            </Badge>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="text-right">
                                    {user.removable && !isExpired ? (
                                        <div className="flex items-center justify-end gap-1">
                                            {onEditUser && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 px-2 text-xs"
                                                    onClick={() =>
                                                        setEditTarget(user)
                                                    }
                                                >
                                                    <Pencil
                                                        className="mr-1 h-3 w-3"
                                                        aria-hidden="true"
                                                    />
                                                    {t('facilities.activeUsersTable.editUser')}
                                                </Button>
                                            )}
                                            {onRevokeAccess && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 px-2 text-xs text-muted-foreground hover:text-rose-400"
                                                    onClick={() =>
                                                        setRevokeAccessTarget(
                                                            user
                                                        )
                                                    }
                                                >
                                                    <X
                                                        className="mr-1 h-3 w-3"
                                                        aria-hidden="true"
                                                    />
                                                    {t('facilities.activeUsersTable.revokeAccess')}
                                                </Button>
                                            )}
                                        </div>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
            <AlertDialog
                open={Boolean(revokeAccessTarget)}
                onOpenChange={(open) => {
                    if (!open) setRevokeAccessTarget(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('facilities.activeUsersTable.revokeTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {revokeAccessTarget
                                ? t('facilities.activeUsersTable.revokeDesc', { email: revokeAccessTarget.email })
                                : t('facilities.activeUsersTable.revokeDescFallback')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={Boolean(revokingAccessId)}>
                            {t('facilities.activeUsersTable.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRevokeAccess}
                            disabled={Boolean(revokingAccessId)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {revokingAccessId ? t('facilities.activeUsersTable.revoking') : t('facilities.activeUsersTable.revoke')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <EditFacilityUserDialog
                open={Boolean(editTarget)}
                user={editTarget}
                roles={roles}
                departmentId={departmentId}
                onOpenChange={(open) => {
                    if (!open) setEditTarget(null);
                }}
                onSave={async (values) => {
                    if (!editTarget || !onEditUser) return;
                    const result = await onEditUser(editTarget, values);
                    if (result !== false) setEditTarget(null);
                }}
            />
        </div>
    );
}
